import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, KeyRound, Loader2, Plus, RefreshCw, Star, Trash2, X } from 'lucide-react';
import { pentestsAPI } from '../../services/api';

/**
 * Stored test credentials.
 *
 * Two or more of these is what makes cross-account authorization testing
 * possible: take a request made as A, replay it as B, diff the responses. That
 * finding class cannot be reached any other way.
 *
 * The secret is write-only. There is no read-back endpoint, so the form clears
 * on save and the row shows a masked preview. If someone loses a credential
 * they re-enter it; that is the intended trade.
 */

/**
 * Worked examples, behind a (?).
 *
 * The form asks for an "auth kind" and a "secret" and an operator who has a
 * login rather than a token cannot tell what to paste. This vault does not
 * drive a login form: it REPLAYS a request, so what it wants is the finished
 * artefact a browser would already be sending, which is a different thing from
 * a username and password and is not obvious from the field names.
 */
const EXAMPLES = [
  {
    kind: 'Custom header',
    when: 'The app sends its own header. Check the Network tab of devtools.',
    header: 'x-wasabi-authorization',
    secret: 'AKIAEXAMPLEKEYID:wJalrEXAMPLESECRETKEY',
  },
  {
    kind: 'Cookie',
    when: 'The app authenticates with a session cookie.',
    header: null,
    secret: 'session=eyJhbGciOi...; csrftoken=abc123',
  },
  {
    kind: 'Bearer token',
    when: 'The app sends Authorization: Bearer. Paste the token only, no prefix.',
    header: null,
    secret: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...',
  },
  {
    kind: 'Basic (pre-encoded)',
    when: 'Paste the base64 of user:pass. It is not encoded for you, because a secret containing a colon cannot be split reliably.',
    header: null,
    secret: 'dXNlcjpwYXNzd29yZA==',
  },
];

function ExamplesHelp() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        title="What do I paste here?"
        className="ml-1.5 inline-flex h-4 w-4 items-center justify-center rounded-full border border-slate-300 text-[10px] font-bold text-slate-500 hover:border-wasabi-400 hover:text-wasabi-700"
      >
        ?
      </button>
      {open && (
        <div className="mt-2 rounded-lg border border-slate-200 bg-white p-3 text-xs">
          <p className="mb-2 leading-snug text-slate-600">
            This replays requests rather than logging in, so paste the finished
            artefact a browser already sends, not a username and password. Open
            devtools, find an authenticated request, and copy from its REQUEST
            headers.
          </p>
          <ul className="space-y-2">
            {EXAMPLES.map((e) => (
              <li key={e.kind} className="border-t border-slate-100 pt-2">
                <div className="font-semibold text-slate-700">{e.kind}</div>
                <div className="text-slate-500">{e.when}</div>
                {e.header && (
                  <div className="mt-0.5">
                    <span className="text-slate-400">header name </span>
                    <code className="rounded bg-slate-100 px-1 text-[11px]">{e.header}</code>
                  </div>
                )}
                <div className="mt-0.5">
                  <span className="text-slate-400">secret </span>
                  <code className="break-all rounded bg-slate-100 px-1 text-[11px]">{e.secret}</code>
                </div>
              </li>
            ))}
          </ul>
          <p className="mt-2 border-t border-slate-100 pt-2 leading-snug text-slate-500">
            Register TWO to unlock cross-account testing: the agent replays one
            account's request as the other and any difference in the answers is
            an access-control finding. With one you get authenticated surface
            and nothing to compare it against.
          </p>
        </div>
      )}
    </>
  );
}

const AUTH_KINDS = [
  { value: 'header', label: 'Custom header', hint: 'e.g. x-wasabi-authorization: KEY:SECRET' },
  { value: 'cookie', label: 'Cookie', hint: 'The full cookie value, e.g. app_session_token=...' },
  { value: 'bearer', label: 'Bearer token', hint: 'Sent as Authorization: Bearer <value>' },
  { value: 'basic', label: 'Basic (pre-encoded)', hint: 'Already base64-encoded user:pass' },
];

const EMPTY = {
  label: '', auth_kind: 'header', secret: '', header_name: '',
  account_ref: '', is_primary: false, notes: '',
};

function AddIdentityForm({ onSubmit, onCancel, busy }) {
  const [form, setForm] = useState(EMPTY);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const kind = AUTH_KINDS.find((k) => k.value === form.auth_kind);
  const needsHeader = form.auth_kind === 'header';
  const valid = form.label.trim() && form.secret.trim() && (!needsHeader || form.header_name.trim());

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
      <div className="grid gap-3 md:grid-cols-2">
        <div>
          <label className="text-xs font-medium text-slate-600">Label</label>
          <input
            value={form.label}
            onChange={(e) => set('label', e.target.value)}
            placeholder="account A / mshala+12"
            className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:border-wasabi-500 focus:outline-none focus:ring-1 focus:ring-wasabi-500"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">Account reference</label>
          <input
            value={form.account_ref}
            onChange={(e) => set('account_ref', e.target.value)}
            placeholder="AcctNum 497794"
            className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:border-wasabi-500 focus:outline-none focus:ring-1 focus:ring-wasabi-500"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">Auth kind</label>
          <select
            value={form.auth_kind}
            onChange={(e) => set('auth_kind', e.target.value)}
            className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:border-wasabi-500 focus:outline-none focus:ring-1 focus:ring-wasabi-500"
          >
            {AUTH_KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
          </select>
          <p className="mt-1 text-[11px] text-slate-400">{kind?.hint}</p>
        </div>
        {needsHeader && (
          <div>
            <label className="text-xs font-medium text-slate-600">Header name</label>
            <input
              value={form.header_name}
              onChange={(e) => set('header_name', e.target.value)}
              placeholder="x-wasabi-authorization"
              className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 font-mono text-sm focus:border-wasabi-500 focus:outline-none focus:ring-1 focus:ring-wasabi-500"
            />
          </div>
        )}
        <div className="md:col-span-2">
          <label className="text-xs font-medium text-slate-600">Secret</label>
          <input
            type="password"
            autoComplete="new-password"
            value={form.secret}
            onChange={(e) => set('secret', e.target.value)}
            placeholder="Stored encrypted. Never shown again after saving."
            className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 font-mono text-sm focus:border-wasabi-500 focus:outline-none focus:ring-1 focus:ring-wasabi-500"
          />
        </div>
        <div className="md:col-span-2">
          <label className="inline-flex items-center gap-2 text-xs text-slate-600">
            <input
              type="checkbox"
              checked={form.is_primary}
              onChange={(e) => set('is_primary', e.target.checked)}
              className="rounded border-slate-300 text-wasabi-600 focus:ring-wasabi-500"
            />
            Primary identity
          </label>
          {/* WHAT THIS FLAG ACTUALLY DOES, which is less than it used to say.
              It sorts the identity first (identity_vault.list) and adds the
              word "primary" to the line an agent reads (agentic/context.py).
              NOTHING at dispatch consults it: BeaconDispatch resolves whatever
              `identity_id` the action carries, and an action that carries none
              goes out unauthenticated. The old copy read "Requests use this one
              unless told otherwise", which is the opposite of true and would
              have an operator believe a probe was authenticated when it was
              not. */}
          <p className="mt-1 text-[11px] leading-relaxed text-slate-500">
            A label for the agents, not a default. It sorts first and is flagged
            &quot;primary&quot; in their context, but it does not authenticate
            anything on its own: an agent has to choose an identity for a given
            request, and one that does not goes out unauthenticated.
          </p>
        </div>
      </div>

      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          disabled={!valid || busy}
          onClick={() => onSubmit(form)}
          className="inline-flex items-center gap-1.5 rounded-md bg-wasabi-500 px-3 py-1.5 text-sm font-semibold text-white hover:bg-wasabi-600 disabled:opacity-50"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          Save identity
        </button>
        <button type="button" onClick={onCancel} className="text-sm text-slate-500 hover:text-slate-800">
          Cancel
        </button>
      </div>
    </div>
  );
}

export default function IdentityVault({ pentestId, isPentester, onCountChange }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  //: Which identity is having its secret replaced, and the draft values. Kept
  //: here rather than in the row so only one panel can be open at a time: two
  //: open secret fields is two chances to paste into the wrong one.
  const [rotating, setRotating] = useState(null);
  const [secret, setSecret] = useState('');
  const [header, setHeader] = useState('');

  const load = useCallback(async () => {
    try {
      setError(null);
      const next = await pentestsAPI.identities.list(pentestId);
      setRows(next);
      // Reported upward so the section can summarise itself while collapsed,
      // and so "none, every request is unauthenticated" is visible without
      // opening it. That state is the one worth seeing from the outside.
      onCountChange?.(next.length);
    } catch (e) {
      setError(
        e?.response?.status === 403
          ? 'Test identities are visible to Red Teamers only.'
          : (e?.response?.data?.detail || 'Could not load identities.'),
      );
    } finally {
      setLoading(false);
    }
  }, [pentestId, onCountChange]);

  useEffect(() => { load(); }, [load]);

  /**
   * Put a new secret behind an existing identity.
   *
   * Rotation rather than delete-and-re-add, which is what an operator had to do
   * before and which renumbers the identity: `identity:1` becomes `identity:2`
   * while the agents' context, any in-flight probe and any finding's
   * `auto_origin` still point at the old id.
   */
  const rotate = async (row) => {
    setBusy(true);
    setError(null);
    try {
      await pentestsAPI.identities.replaceSecret(pentestId, row.id, {
        secret: secret.trim(),
        // Only sent for a header identity; omitted means unchanged.
        ...(row.auth_kind === 'header' ? { header_name: header.trim() } : {}),
      });
      setRotating(null);
      setSecret('');
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not replace the secret.');
    } finally {
      setBusy(false);
    }
  };

  const create = async (form) => {
    setBusy(true);
    try {
      await pentestsAPI.identities.create(pentestId, {
        ...form,
        header_name: form.auth_kind === 'header' ? form.header_name : null,
        account_ref: form.account_ref || null,
        notes: form.notes || null,
      });
      setAdding(false);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not save the identity.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row) => {
    setBusy(true);
    try {
      await pentestsAPI.identities.remove(pentestId, row.id);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not delete the identity.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading identities...
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {error && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span className="flex-1">{error}</span>
          <button type="button" onClick={() => setError(null)}><X className="h-4 w-4" /></button>
        </div>
      )}

      {rows.length === 0 && !adding && (
        <p className="text-sm text-slate-500">
          No identities yet. Register two or more and the agent can replay one account&apos;s
          request as another, which is how cross-account access control gets tested.
        </p>
      )}

      {rows.length > 0 && (
        <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
          {rows.map((row) => (
            <div key={row.id}>
            <div className="flex items-center gap-3 px-3 py-2">
              <KeyRound className="h-4 w-4 shrink-0 text-slate-400" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 text-sm font-medium text-slate-900">
                  {row.label}
                  {row.is_primary && (
                    <span title="Primary identity: a label for the agents, not a default. Requests are unauthenticated unless an agent picks an identity.">
                      <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-500" />
                    </span>
                  )}
                </div>
                <div className="truncate text-xs text-slate-500">
                  {row.auth_kind}
                  {row.header_name ? ` · ${row.header_name}` : ''}
                  {row.account_ref ? ` · ${row.account_ref}` : ''}
                </div>
              </div>
              <span className="hidden font-mono text-xs text-slate-400 sm:inline">
                {row.secret_preview}
              </span>
              {!row.usable && (
                <span
                  className="rounded border border-red-200 bg-red-50 px-1.5 py-0.5 text-[11px] font-semibold text-red-700"
                  title="The stored secret cannot be decrypted. ENCRYPTION_SALT or SECRET_KEY changed since it was saved. Re-enter it."
                >
                  unusable
                </span>
              )}
              {isPentester && (
                <>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => { setRotating(row.id); setSecret(''); setHeader(row.header_name || ''); }}
                    className="text-slate-400 hover:text-wasabi-700 disabled:opacity-40"
                    title="Paste a new secret. Keeps this identity and its id."
                  >
                    <RefreshCw className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => remove(row)}
                    className="text-slate-400 hover:text-red-600 disabled:opacity-40"
                    title="Delete identity"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </>
              )}
            </div>
            {rotating === row.id && (
              <div className="border-t border-slate-200 bg-slate-50 p-3">
                <div className="mb-2 text-xs font-semibold text-slate-700">
                  New secret for {row.label}
                </div>
                {/* Says what is NOT changing, because that is the reason to use
                    this instead of delete-and-re-add: the id survives, so every
                    reference to identity:{row.id} keeps working. */}
                <p className="mb-2 text-[11px] leading-snug text-slate-500">
                  Stays identity:{row.id}, {row.auth_kind}
                  {row.account_ref ? `, ${row.account_ref}` : ''}. Only the
                  secret changes, so anything already referring to this identity
                  keeps working.
                </p>
                {row.auth_kind === 'header' && (
                  <input
                    value={header}
                    onChange={(e) => setHeader(e.target.value)}
                    placeholder="header name"
                    className="mb-2 w-full rounded-md border border-slate-300 px-2 py-1.5 font-mono text-xs"
                  />
                )}
                <input
                  value={secret}
                  onChange={(e) => setSecret(e.target.value)}
                  autoFocus
                  placeholder="Paste the new secret. Stored encrypted, never shown again."
                  className="w-full rounded-md border border-slate-300 px-2 py-1.5 font-mono text-xs"
                />
                <div className="mt-2 flex items-center gap-2">
                  <button
                    type="button"
                    disabled={!secret.trim() || busy}
                    onClick={() => rotate(row)}
                    className="inline-flex items-center gap-1.5 rounded-md bg-wasabi-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-wasabi-600 disabled:opacity-50"
                  >
                    {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                    Replace secret
                  </button>
                  <button
                    type="button"
                    onClick={() => { setRotating(null); setSecret(''); }}
                    className="text-xs text-slate-500 hover:text-slate-700"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
          ))}
        </div>
      )}

      {adding ? (
        <AddIdentityForm onSubmit={create} onCancel={() => setAdding(false)} busy={busy} />
      ) : isPentester && (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          <Plus className="h-4 w-4" /> Add identity
        </button>
      )}
      <div className="mt-2 flex items-start text-xs text-slate-500">
        <span>What do I paste here?</span>
        <ExamplesHelp />
      </div>
    </div>
  );
}
